/*
 * ตาราง (T1, choices 27–28 ต.ค. 2569: T1 ตารางชิ้นงานชนิดใหม่ · E1 แผงแก้แบบ Excel · L1 ข้ามหน้าอัตโนมัติ (PR 3) ·
 * I1 นำเข้า .xlsx ด้วยตัวอ่านเอง (PR 3) · รวมช่องได้ · ไม่มีสูตร).
 *
 * A table is a fabric.Group on the page that carries this data as `table` (src/table/tableFabric.ts); the rectangles
 * and Thai text boxes inside it are drawn from the data, so the picture, print PDF and document PDF draw it as they
 * draw any group. This file is plain data — no fabric, no DOM — so the app, the server (worker/rules.ts checkDesign)
 * and the tests use the same checker (BUG-13) and the same edits.
 *
 * Every edit returns a NEW table (the data is never changed in place: a copied table shares nothing with its copy).
 */
import { fontInfo } from '../fonts/catalog';

export const TABLE_MAX_ROWS = 300;
export const TABLE_MAX_COLS = 26;
/** characters in one cell */
export const TABLE_CELL_MAX = 1000;
/** a column's width / a row's least height, in page px */
export const TABLE_COL_MIN = 20, TABLE_SIDE_MAX = 20000;
export const TABLE_SIZE_MIN = 6, TABLE_SIZE_MAX = 400;
export const TABLE_PAD_MAX = 200, TABLE_LINE_MAX = 40;
/** the text's line height in a cell (src/table/tableFabric.ts) */
export const TABLE_LINE_HEIGHT = 1.3;

export type CellAlign = 'l' | 'c' | 'r';
export type CellVAlign = 't' | 'm' | 'b';
export const CELL_ALIGNS: readonly CellAlign[] = ['l', 'c', 'r'];
export const CELL_VALIGNS: readonly CellVAlign[] = ['t', 'm', 'b'];

/** one cell; a style left out = the table's own (font size `size`, ink `color`, no fill, left, top) */
export interface TableCell {
  t: string;
  /** เอกสารสองภาษา (src/docs/docFields.ts switchLang): the template's words in each language */
  tr?: { th: string; en: string };
  /** bold */
  b?: true;
  al?: CellAlign;
  va?: CellVAlign;
  /** fill #rrggbb */
  bg?: string;
  /** ink #rrggbb */
  c?: string;
  /** font size, page px */
  fs?: number;
}
/** cells joined into one: the top-left one shows, the others are covered */
export interface TableMerge { r: number; c: number; rs: number; cs: number }
export interface TableData {
  v: 1;
  /** column widths, page px */
  cols: number[];
  /** each row's least height, page px (0 = as tall as its text) */
  rows: number[];
  cells: TableCell[][];
  merges: TableMerge[];
  /** header rows: repeated at the top of each page when the table goes on to the next page (PR 3) */
  head: number;
  font: string;
  size: number;
  color: string;
  /** grid line colour and width (0 = no lines) */
  line: string;
  lw: number;
  /** space between a cell's edge and its text */
  pad: number;
}
/** cells chosen: rows r0..r1, columns c0..c1 (inclusive, r0 ≤ r1, c0 ≤ c1) */
export interface TableSel { r0: number; c0: number; r1: number; c1: number }
export type Checked<T> = { ok: true; value: T } | { ok: false; error: string };
/** an edit: the new table and what the person should know about it, or why it was not done */
export type Edited = { ok: true; value: TableData; notes: string[] } | { ok: false; error: string };

const HEX = /^#[0-9a-f]{6}$/;
const isObj = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);
const num = (v: unknown, lo: number, hi: number) => typeof v === 'number' && Number.isFinite(v) && v >= lo && v <= hi;
const int = (v: unknown, lo: number, hi: number) => Number.isInteger(v) && (v as number) >= lo && (v as number) <= hi;

/** A, B … Z, AA — as Excel names columns */
export function colName(c: number): string {
  let s = '', n = c + 1;
  while (n > 0) { const m = (n - 1) % 26; s = String.fromCharCode(65 + m) + s; n = Math.floor((n - 1) / 26); }
  return s;
}
/** "B3" */
export const cellName = (r: number, c: number) => `${colName(c)}${r + 1}`;
const where = (r: number, c: number) => `ช่อง ${cellName(r, c)}`;

/* ---------- the checker (the app when editing and opening, the server when saving — BUG-13) ---------- */

/**
 * The table, or why it cannot be used. The value is a clean copy with only the known fields, so checking it again
 * gives the same table (BUG-72: check(check(x)) = check(x)).
 */
export function checkTable(raw: unknown): Checked<TableData> {
  if (!isObj(raw)) return { ok: false, error: 'ข้อมูลตารางเสีย (ไม่ใช่ข้อมูลตาราง)' };
  if (raw.v !== 1) return { ok: false, error: 'ข้อมูลตารางเป็นรุ่นที่แอปนี้ไม่รู้จัก ลองรีเฟรชหน้าเว็บเพื่ออัปเดตแอป' };
  const cols = raw.cols, rows = raw.rows;
  if (!Array.isArray(cols) || cols.length < 1 || cols.length > TABLE_MAX_COLS) return { ok: false, error: `ตารางต้องมี 1–${TABLE_MAX_COLS} คอลัมน์` };
  if (!Array.isArray(rows) || rows.length < 1 || rows.length > TABLE_MAX_ROWS) return { ok: false, error: `ตารางต้องมี 1–${TABLE_MAX_ROWS} แถว` };
  for (const [c, w] of cols.entries()) if (!num(w, TABLE_COL_MIN, TABLE_SIDE_MAX)) return { ok: false, error: `ความกว้างคอลัมน์ ${colName(c)} ต้องอยู่ระหว่าง ${TABLE_COL_MIN}–${TABLE_SIDE_MAX}` };
  for (const [r, h] of rows.entries()) if (!num(h, 0, TABLE_SIDE_MAX)) return { ok: false, error: `ความสูงแถว ${r + 1} ต้องอยู่ระหว่าง 0–${TABLE_SIDE_MAX}` };
  if (typeof raw.font !== 'string' || !fontInfo(raw.font)) return { ok: false, error: `ฟอนต์ของตาราง "${String(raw.font ?? '')}" ไม่มีในระบบ` };
  if (!fontInfo(raw.font)!.thai) return { ok: false, error: `ฟอนต์ ${raw.font} ไม่มีตัวไทย ใช้กับตารางไม่ได้` };
  if (!num(raw.size, TABLE_SIZE_MIN, TABLE_SIZE_MAX)) return { ok: false, error: `ขนาดตัวอักษรของตารางต้องอยู่ระหว่าง ${TABLE_SIZE_MIN}–${TABLE_SIZE_MAX}` };
  if (typeof raw.color !== 'string' || !HEX.test(raw.color)) return { ok: false, error: 'สีตัวอักษรของตารางต้องอยู่ในรูปแบบ #rrggbb' };
  if (typeof raw.line !== 'string' || !HEX.test(raw.line)) return { ok: false, error: 'สีเส้นตารางต้องอยู่ในรูปแบบ #rrggbb' };
  if (!num(raw.lw, 0, TABLE_LINE_MAX)) return { ok: false, error: `ความหนาเส้นตารางต้องอยู่ระหว่าง 0–${TABLE_LINE_MAX}` };
  if (!num(raw.pad, 0, TABLE_PAD_MAX)) return { ok: false, error: `ระยะห่างจากขอบช่องต้องอยู่ระหว่าง 0–${TABLE_PAD_MAX}` };
  if (!int(raw.head, 0, rows.length)) return { ok: false, error: `จำนวนแถวหัวตารางต้องเป็น 0–${rows.length}` };
  const cells = raw.cells;
  if (!Array.isArray(cells) || cells.length !== rows.length) return { ok: false, error: 'ข้อมูลตารางเสีย (จำนวนแถวของช่องไม่ตรงกับจำนวนแถว)' };
  const clean: TableCell[][] = [];
  for (const [r, row] of cells.entries()) {
    if (!Array.isArray(row) || row.length !== cols.length) return { ok: false, error: `ข้อมูลตารางเสีย (แถว ${r + 1} มีจำนวนช่องไม่ตรงกับจำนวนคอลัมน์)` };
    const out: TableCell[] = [];
    for (const [c, cell] of row.entries()) {
      const k = checkCell(cell, r, c);
      if (!k.ok) return k;
      out.push(k.value);
    }
    clean.push(out);
  }
  const merges = raw.merges;
  if (!Array.isArray(merges)) return { ok: false, error: 'ข้อมูลตารางเสีย (รายการช่องที่รวมไว้)' };
  const taken = new Set<string>();
  const cleanMerges: TableMerge[] = [];
  for (const m of merges) {
    if (!isObj(m) || !int(m.r, 0, rows.length - 1) || !int(m.c, 0, cols.length - 1) || !int(m.rs, 1, rows.length) || !int(m.cs, 1, cols.length)) {
      return { ok: false, error: 'ข้อมูลตารางเสีย (ช่องที่รวมไว้อยู่นอกตาราง)' };
    }
    const g = m as unknown as TableMerge;
    if (g.rs * g.cs < 2) return { ok: false, error: `ข้อมูลตารางเสีย (${where(g.r, g.c)} รวมไว้แค่ช่องเดียว)` };
    if (g.r + g.rs > rows.length || g.c + g.cs > cols.length) return { ok: false, error: `ช่องที่รวมจาก ${where(g.r, g.c)} ล้นออกนอกตาราง` };
    for (let r = g.r; r < g.r + g.rs; r++) for (let c = g.c; c < g.c + g.cs; c++) {
      const key = `${r},${c}`;
      if (taken.has(key)) return { ok: false, error: `ข้อมูลตารางเสีย (${where(r, c)} อยู่ในช่องที่รวมไว้สองที่)` };
      taken.add(key);
    }
    cleanMerges.push({ r: g.r, c: g.c, rs: g.rs, cs: g.cs });
  }
  return {
    ok: true,
    value: {
      v: 1, cols: [...cols] as number[], rows: [...rows] as number[], cells: clean, merges: cleanMerges, head: raw.head as number,
      font: raw.font, size: raw.size as number, color: raw.color, line: raw.line, lw: raw.lw as number, pad: raw.pad as number
    }
  };
}

function checkCell(raw: unknown, r: number, c: number): Checked<TableCell> {
  if (!isObj(raw) || typeof raw.t !== 'string') return { ok: false, error: `ข้อมูลตารางเสีย (${where(r, c)})` };
  if ([...raw.t].length > TABLE_CELL_MAX) return { ok: false, error: `${where(r, c)} ยาวเกิน ${TABLE_CELL_MAX} ตัวอักษร` };
  const out: TableCell = { t: raw.t };
  if (raw.b !== undefined) { if (raw.b !== true) return { ok: false, error: `ข้อมูลตารางเสีย (ตัวหนาของ${where(r, c)})` }; out.b = true; }
  if (raw.al !== undefined) { if (!CELL_ALIGNS.includes(raw.al as CellAlign)) return { ok: false, error: `การจัดแนวของ${where(r, c)} ไม่ถูกต้อง` }; out.al = raw.al as CellAlign; }
  if (raw.va !== undefined) { if (!CELL_VALIGNS.includes(raw.va as CellVAlign)) return { ok: false, error: `การจัดแนวตั้งของ${where(r, c)} ไม่ถูกต้อง` }; out.va = raw.va as CellVAlign; }
  if (raw.bg !== undefined) { if (typeof raw.bg !== 'string' || !HEX.test(raw.bg)) return { ok: false, error: `สีพื้นของ${where(r, c)} ต้องอยู่ในรูปแบบ #rrggbb` }; out.bg = raw.bg; }
  if (raw.c !== undefined) { if (typeof raw.c !== 'string' || !HEX.test(raw.c)) return { ok: false, error: `สีตัวอักษรของ${where(r, c)} ต้องอยู่ในรูปแบบ #rrggbb` }; out.c = raw.c; }
  if (raw.fs !== undefined) { if (!num(raw.fs, TABLE_SIZE_MIN, TABLE_SIZE_MAX)) return { ok: false, error: `ขนาดตัวอักษรของ${where(r, c)} ต้องอยู่ระหว่าง ${TABLE_SIZE_MIN}–${TABLE_SIZE_MAX}` }; out.fs = raw.fs as number; }
  if (raw.tr !== undefined) {
    const tr = raw.tr;
    if (!isObj(tr) || typeof tr.th !== 'string' || typeof tr.en !== 'string' || [...tr.th].length > TABLE_CELL_MAX || [...tr.en].length > TABLE_CELL_MAX) {
      return { ok: false, error: `คำแปลของ${where(r, c)} เสีย หรือยาวเกิน ${TABLE_CELL_MAX} ตัวอักษร` };
    }
    out.tr = { th: tr.th, en: tr.en };
  }
  return { ok: true, value: out };
}

/* ---------- a new table ---------- */

/** `u`: the page's unit (src/editor/editor.ts unit(): the long side ÷ 1080), so a table looks the same on any page */
export function newTable(cols: number, rows: number, u: number): TableData {
  const r1 = Math.round(1000 * u) / 1000;
  // a white body (a table put over a picture or text stays readable) under a grey, bold header row
  const cells = Array.from({ length: rows }, (_, r) => Array.from({ length: cols }, (): TableCell => r === 0 ? { t: '', b: true, bg: '#eef0f4' } : { t: '', bg: '#ffffff' }));
  return {
    v: 1, cols: Array(cols).fill(Math.max(TABLE_COL_MIN, Math.round(220 * r1))), rows: Array(rows).fill(0), cells, merges: [],
    head: rows > 1 ? 1 : 0, font: 'Sarabun', size: Math.max(TABLE_SIZE_MIN, Math.round(30 * r1)), color: '#1f2a1c',
    line: '#9aa0ad', lw: Math.max(1, Math.round(2 * r1 * 10) / 10), pad: Math.round(12 * r1)
  };
}

/* ---------- merges ---------- */

/** the merge a cell is in, or null */
export const mergeAt = (t: TableData, r: number, c: number): TableMerge | null =>
  t.merges.find(m => r >= m.r && r < m.r + m.rs && c >= m.c && c < m.c + m.cs) ?? null;
/** covered by a merge (only its top-left cell shows) */
export function isCovered(t: TableData, r: number, c: number): boolean {
  const m = mergeAt(t, r, c);
  return !!m && (m.r !== r || m.c !== c);
}
export const normSel = (s: TableSel): TableSel => ({ r0: Math.min(s.r0, s.r1), c0: Math.min(s.c0, s.c1), r1: Math.max(s.r0, s.r1), c1: Math.max(s.c0, s.c1) });
const overlaps = (m: TableMerge, s: TableSel) => m.r <= s.r1 && m.r + m.rs - 1 >= s.r0 && m.c <= s.c1 && m.c + m.cs - 1 >= s.c0;
/** grown to take in every merge it touches (as Excel does) */
export function growSel(t: TableData, sel: TableSel): TableSel {
  let s = normSel(sel), again = true;
  while (again) {
    again = false;
    for (const m of t.merges) {
      if (!overlaps(m, s)) continue;
      const n = { r0: Math.min(s.r0, m.r), c0: Math.min(s.c0, m.c), r1: Math.max(s.r1, m.r + m.rs - 1), c1: Math.max(s.c1, m.c + m.cs - 1) };
      if (n.r0 !== s.r0 || n.c0 !== s.c0 || n.r1 !== s.r1 || n.c1 !== s.c1) { s = n; again = true; }
    }
  }
  return s;
}

const copy = (t: TableData): TableData => ({ ...t, cols: [...t.cols], rows: [...t.rows], cells: t.cells.map(row => row.map(c => ({ ...c }))), merges: t.merges.map(m => ({ ...m })) });
// a cell's look without its words (nor their translation)
const styleOf = (c: TableCell): TableCell => { const { t: _t, tr: _tr, ...s } = c; return { t: '', ...s }; };
const done = (value: TableData, notes: string[] = []): Edited => ({ ok: true, value, notes });

export function mergeCells(t: TableData, sel: TableSel): Edited {
  const s = growSel(t, sel);
  if (s.r0 === s.r1 && s.c0 === s.c1) return { ok: false, error: 'เลือกมากกว่า 1 ช่องก่อนกดรวมช่อง (แตะ "เลือกหลายช่อง" แล้วแตะช่องสุดท้าย)' };
  const n = copy(t);
  n.merges = n.merges.filter(m => !overlaps(m, s));
  // nothing typed is dropped: every text in the cells joined goes into the first one, a line each
  const texts: string[] = [];
  for (let r = s.r0; r <= s.r1; r++) for (let c = s.c0; c <= s.c1; c++) {
    if (n.cells[r][c].t.trim()) texts.push(n.cells[r][c].t);
    if (r !== s.r0 || c !== s.c0) n.cells[r][c].t = '';
  }
  const joined = texts.join('\n');
  if ([...joined].length > TABLE_CELL_MAX) return { ok: false, error: `ข้อความรวมกันยาวเกิน ${TABLE_CELL_MAX} ตัวอักษร รวมช่องไม่ได้ (ย่อข้อความก่อน)` };
  n.cells[s.r0][s.c0].t = joined;
  n.merges.push({ r: s.r0, c: s.c0, rs: s.r1 - s.r0 + 1, cs: s.c1 - s.c0 + 1 });
  return done(n, texts.length > 1 ? [`รวมข้อความจาก ${texts.length} ช่องไว้ในช่อง ${cellName(s.r0, s.c0)} (ขึ้นบรรทัดใหม่ทีละช่อง)`] : []);
}

export function splitCells(t: TableData, sel: TableSel): Edited {
  const s = normSel(sel);
  const hit = t.merges.filter(m => overlaps(m, s));
  if (!hit.length) return { ok: false, error: 'ช่องที่เลือกไม่ได้รวมไว้ จึงไม่มีอะไรให้แยก' };
  const n = copy(t);
  n.merges = n.merges.filter(m => !overlaps(m, s));
  return done(n);
}

/* ---------- rows and columns ---------- */

export function insertRows(t: TableData, at: number, count = 1): Edited {
  if (!int(at, 0, t.rows.length) || count < 1) return { ok: false, error: 'ตำแหน่งแทรกแถวไม่ถูกต้อง' };
  if (t.rows.length + count > TABLE_MAX_ROWS) return { ok: false, error: `ตารางมีได้สูงสุด ${TABLE_MAX_ROWS} แถว (ตอนนี้มี ${t.rows.length})` };
  const n = copy(t);
  // the new rows look like the row above, as Excel does — except right under the header rows (they look like the
  // body row below) and at the very top (like the row below)
  const like = at === n.head && at < n.rows.length ? n.cells[at] : n.cells[Math.max(0, at - 1)];
  const fresh = Array.from({ length: count }, () => like.map(styleOf));
  n.cells.splice(at, 0, ...fresh);
  n.rows.splice(at, 0, ...Array(count).fill(n.rows[Math.max(0, at - 1)] ?? 0));
  n.merges = n.merges.map(m => m.r >= at ? { ...m, r: m.r + count } : m.r + m.rs > at ? { ...m, rs: m.rs + count } : m);
  if (at < n.head) n.head += count;
  return done(n);
}

export function insertCols(t: TableData, at: number, count = 1): Edited {
  if (!int(at, 0, t.cols.length) || count < 1) return { ok: false, error: 'ตำแหน่งแทรกคอลัมน์ไม่ถูกต้อง' };
  if (t.cols.length + count > TABLE_MAX_COLS) return { ok: false, error: `ตารางมีได้สูงสุด ${TABLE_MAX_COLS} คอลัมน์ (ตอนนี้มี ${t.cols.length})` };
  const n = copy(t);
  const from = Math.max(0, at - 1);
  n.cells = n.cells.map(row => { const out = [...row]; out.splice(at, 0, ...Array.from({ length: count }, () => styleOf(row[from]))); return out; });
  n.cols.splice(at, 0, ...Array(count).fill(n.cols[from]));
  n.merges = n.merges.map(m => m.c >= at ? { ...m, c: m.c + count } : m.c + m.cs > at ? { ...m, cs: m.cs + count } : m);
  return done(n);
}

/** a merge after rows/columns a..b (inclusive) are taken out along one side; null = gone */
function cutMerge(start: number, span: number, a: number, b: number): { start: number; span: number } | null {
  const end = start + span - 1;
  const kept = span - Math.max(0, Math.min(end, b) - Math.max(start, a) + 1);
  if (kept <= 0) return null;
  return { start: start > b ? start - (b - a + 1) : Math.min(start, a), span: kept };
}

export function deleteRows(t: TableData, r0: number, r1: number): Edited {
  const a = Math.min(r0, r1), b = Math.max(r0, r1);
  if (!int(a, 0, t.rows.length - 1) || !int(b, 0, t.rows.length - 1)) return { ok: false, error: 'แถวที่จะลบไม่ถูกต้อง' };
  if (b - a + 1 >= t.rows.length) return { ok: false, error: 'ตารางต้องเหลืออย่างน้อย 1 แถว (ถ้าไม่ใช้ทั้งตาราง ให้ลบตารางที่หน้างาน)' };
  const n = copy(t);
  const merges: TableMerge[] = [];
  for (const m of n.merges) {
    const k = cutMerge(m.r, m.rs, a, b);
    if (!k) continue;
    // the merge's first row is going but the merge stays: its text moves down to the merge's new first cell
    if (m.r >= a && m.r <= b) n.cells[b + 1][m.c] = { ...n.cells[b + 1][m.c], t: n.cells[m.r][m.c].t };
    if (k.span * m.cs >= 2) merges.push({ r: k.start, c: m.c, rs: k.span, cs: m.cs });
  }
  n.cells.splice(a, b - a + 1);
  n.rows.splice(a, b - a + 1);
  n.merges = merges;
  n.head = Math.max(0, n.head - Math.max(0, Math.min(b, n.head - 1) - a + 1));
  return done(n);
}

export function deleteCols(t: TableData, c0: number, c1: number): Edited {
  const a = Math.min(c0, c1), b = Math.max(c0, c1);
  if (!int(a, 0, t.cols.length - 1) || !int(b, 0, t.cols.length - 1)) return { ok: false, error: 'คอลัมน์ที่จะลบไม่ถูกต้อง' };
  if (b - a + 1 >= t.cols.length) return { ok: false, error: 'ตารางต้องเหลืออย่างน้อย 1 คอลัมน์ (ถ้าไม่ใช้ทั้งตาราง ให้ลบตารางที่หน้างาน)' };
  const n = copy(t);
  const merges: TableMerge[] = [];
  for (const m of n.merges) {
    const k = cutMerge(m.c, m.cs, a, b);
    if (!k) continue;
    if (m.c >= a && m.c <= b) n.cells[m.r][b + 1] = { ...n.cells[m.r][b + 1], t: n.cells[m.r][m.c].t };
    if (k.span * m.rs >= 2) merges.push({ r: m.r, c: k.start, rs: m.rs, cs: k.span });
  }
  n.cells = n.cells.map(row => { const out = [...row]; out.splice(a, b - a + 1); return out; });
  n.cols.splice(a, b - a + 1);
  n.merges = merges;
  return done(n);
}

/* ---------- text and looks ---------- */

export function setText(t: TableData, r: number, c: number, text: string): Edited {
  if (!int(r, 0, t.rows.length - 1) || !int(c, 0, t.cols.length - 1)) return { ok: false, error: 'ช่องที่เลือกไม่อยู่ในตารางแล้ว แตะเลือกช่องใหม่' };
  const len = [...text].length;
  if (len > TABLE_CELL_MAX) return { ok: false, error: `${where(r, c)} ยาวเกิน ${TABLE_CELL_MAX} ตัวอักษร (ตอนนี้ ${len}) ข้อความส่วนที่เกินยังไม่ได้ใส่` };
  const n = copy(t);
  n.cells[r][c].t = text;
  return done(n);
}

/** a look for every cell chosen; null takes it away (back to the table's own) */
export type CellLook = { [K in 'b' | 'al' | 'va' | 'bg' | 'c' | 'fs']?: TableCell[K] | null };
export function setLook(t: TableData, sel: TableSel, look: CellLook): Edited {
  const s = growSel(t, sel);
  if (look.bg && !HEX.test(look.bg)) return { ok: false, error: 'สีพื้นต้องอยู่ในรูปแบบ #rrggbb' };
  if (look.c && !HEX.test(look.c)) return { ok: false, error: 'สีตัวอักษรต้องอยู่ในรูปแบบ #rrggbb' };
  if (look.fs != null && !num(look.fs, TABLE_SIZE_MIN, TABLE_SIZE_MAX)) return { ok: false, error: `ขนาดตัวอักษรต้องอยู่ระหว่าง ${TABLE_SIZE_MIN}–${TABLE_SIZE_MAX}` };
  const n = copy(t);
  for (let r = s.r0; r <= s.r1; r++) for (let c = s.c0; c <= s.c1; c++) {
    const cell = n.cells[r][c] as unknown as Record<string, unknown>;
    for (const [k, v] of Object.entries(look)) { if (v === null) delete cell[k]; else if (v !== undefined) cell[k] = v; }
  }
  return done(n);
}

export function clearText(t: TableData, sel: TableSel): Edited {
  const s = growSel(t, sel);
  const n = copy(t);
  for (let r = s.r0; r <= s.r1; r++) for (let c = s.c0; c <= s.c1; c++) n.cells[r][c].t = '';
  return done(n);
}

export function setColWidth(t: TableData, c0: number, c1: number, w: number): Edited {
  if (!num(w, TABLE_COL_MIN, TABLE_SIDE_MAX)) return { ok: false, error: `ความกว้างคอลัมน์ต้องอยู่ระหว่าง ${TABLE_COL_MIN}–${TABLE_SIDE_MAX}` };
  const n = copy(t);
  for (let c = Math.min(c0, c1); c <= Math.max(c0, c1); c++) n.cols[c] = Math.round(w * 10) / 10;
  return done(n);
}

/** the columns chosen share their width equally */
export function evenCols(t: TableData, c0: number, c1: number): Edited {
  const a = Math.min(c0, c1), b = Math.max(c0, c1);
  if (a === b) return { ok: false, error: 'เลือกอย่างน้อย 2 คอลัมน์ก่อน (แตะหัวคอลัมน์ แล้วแตะ "เลือกหลายช่อง" และหัวคอลัมน์สุดท้าย)' };
  const sum = t.cols.slice(a, b + 1).reduce((x, y) => x + y, 0);
  return setColWidth(t, a, b, sum / (b - a + 1));
}

/** the table's own settings (font, size, colours, lines, padding, header rows) */
export function setTableLook(t: TableData, look: Partial<Pick<TableData, 'font' | 'size' | 'color' | 'line' | 'lw' | 'pad' | 'head'>>): Edited {
  const k = checkTable({ ...t, ...look });
  return k.ok ? done(k.value) : k;
}

/* ---------- วางจาก Excel ---------- */

/** what was copied: cells (text and, from Excel's HTML, looks) and the merges among them (relative to the first cell) */
export interface PasteGrid { cells: TableCell[][]; merges: TableMerge[]; looks: boolean }

/**
 * Puts what was copied into the table from `at` (its top-left cell), adding rows and columns when it runs past the
 * edge. All or nothing (CORE: no half imports): a cell too long, or a table that would be too big, refuses the whole
 * paste with every reason.
 */
export function pasteGrid(t: TableData, at: { r: number; c: number }, g: PasteGrid): Edited {
  const gr = g.cells.length, gc = g.cells[0]?.length ?? 0;
  if (!gr || !gc) return { ok: false, error: 'สิ่งที่คัดลอกมาไม่มีช่องข้อมูล' };
  if (at.r + gr > TABLE_MAX_ROWS || at.c + gc > TABLE_MAX_COLS) {
    return { ok: false, error: `ข้อมูลที่วางมี ${gr} แถว × ${gc} คอลัมน์ วางที่ ${cellName(at.r, at.c)} แล้วตารางจะเกิน ${TABLE_MAX_ROWS} แถว × ${TABLE_MAX_COLS} คอลัมน์ ยังไม่ได้วาง` };
  }
  const long: string[] = [];
  g.cells.forEach((row, r) => row.forEach((cell, c) => { if ([...cell.t].length > TABLE_CELL_MAX) long.push(cellName(at.r + r, at.c + c)); }));
  if (long.length) {
    return { ok: false, error: `ช่อง ${long.slice(0, 10).join(', ')}${long.length > 10 ? ` และอีก ${long.length - 10} ช่อง` : ''} ยาวเกิน ${TABLE_CELL_MAX} ตัวอักษร ยังไม่ได้วาง (ย่อใน Excel ก่อน)` };
  }
  let n = t;
  const notes: string[] = [];
  const addRows = at.r + gr - n.rows.length, addCols = at.c + gc - n.cols.length;
  if (addRows > 0) { const k = insertRows(n, n.rows.length, addRows); if (!k.ok) return k; n = k.value; notes.push(`เพิ่ม ${addRows} แถว`); }
  if (addCols > 0) { const k = insertCols(n, n.cols.length, addCols); if (!k.ok) return k; n = k.value; notes.push(`เพิ่ม ${addCols} คอลัมน์`); }
  n = copy(n);
  const area: TableSel = { r0: at.r, c0: at.c, r1: at.r + gr - 1, c1: at.c + gc - 1 };
  const cut = n.merges.filter(m => overlaps(m, area));
  if (cut.length) notes.push(`แยกช่องที่รวมไว้เดิม ${cut.length} ที่ในบริเวณที่วาง`);
  n.merges = n.merges.filter(m => !overlaps(m, area));
  g.cells.forEach((row, r) => row.forEach((cell, c) => {
    const target = n.cells[at.r + r][at.c + c];
    n.cells[at.r + r][at.c + c] = g.looks ? { ...cell } : { ...target, t: cell.t };
  }));
  for (const m of g.merges) n.merges.push({ r: at.r + m.r, c: at.c + m.c, rs: m.rs, cs: m.cs });
  const k = checkTable(n);
  if (!k.ok) return { ok: false, error: `วางแล้วตารางใช้ไม่ได้: ${k.error} ยังไม่ได้วาง` };
  return done(k.value, notes);
}

/* ---------- the whole design (the server, worker/rules.ts checkDesign) ---------- */

/**
 * Every table in a design's JSON (all pages) through the same checker as the app (BUG-13); the first problem with
 * the page it is on, or null.
 */
export function tablesProblem(design: unknown): string | null {
  if (!isObj(design)) return null;
  const pages: unknown[] = [design.objects, ...(Array.isArray(design.extraPages) ? design.extraPages.map(p => isObj(p) ? p.objects : null) : [])];
  for (const [i, objects] of pages.entries()) {
    if (!Array.isArray(objects)) continue;
    for (const o of objects) {
      if (!isObj(o) || o.table === undefined) continue;
      const k = checkTable(o.table);
      if (!k.ok) return `ตารางในหน้า ${i + 1}: ${k.error}`;
    }
  }
  return null;
}
